import React from 'react';

/**
 * The assistant's words, with the values in them shown as values.
 *
 * A reply that says "your primary colour is #000000" is telling the vendor a
 * colour and showing them a string. The colour is the answer, and it costs a
 * swatch to show it — so a hex code renders as its own swatch beside its code,
 * and the sentence reads as the thing it is about.
 *
 * The panel renders text exactly as the model types it, which is deliberate:
 * there is no markdown renderer here and adding one would make every reply a
 * document. This is the narrow version — a short list of patterns worth
 * lifting out of a sentence, and nothing else. Adding another is one entry in
 * MATCHERS.
 *
 * It also cleans up after the model. It is told not to write markdown, and it
 * mostly does not, but `**bold**` slipping through renders as literal asterisks
 * in front of the vendor. Emphasis is quietly honoured rather than shown.
 */

/** A hex colour, not preceded or followed by anything that makes it part of a word. */
const HEX = /(?<![\w#])#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})(?![0-9a-fA-F\w])/g;

/** `**bold**`, which the model is told not to write and occasionally writes. */
const BOLD = /\*\*(.+?)\*\*/g;

interface Matcher {
    pattern: RegExp;
    render: (match: string, key: string) => React.ReactNode;
}

/**
 * A colour, as a chip that shows it.
 *
 * The code stays visible next to the swatch rather than being replaced by it:
 * a vendor comparing this against a brand guideline needs to read the digits,
 * and a bare square would make them hover to find out which colour it is.
 */
const ColorBadge: React.FC<{ value: string }> = ({ value }) => (
    <span className="inline-flex items-center gap-1 rounded-full border bg-background px-1.5 py-0.5 align-middle text-[12px] leading-none">
        <span
            aria-hidden
            className="size-3 shrink-0 rounded-full border"
            style={{ backgroundColor: value }}
        />
        <span className="font-mono">{value}</span>
    </span>
);

const MATCHERS: Matcher[] = [
    {
        pattern: BOLD,
        render: (match, key) => (
            /*
             * The contents go back through the matchers.
             *
             * Bold runs first, so anything it swallowed would never reach the
             * ones after it — and the assistant is told to bold the figure the
             * vendor is being told, which is exactly where a colour ends up.
             * "**#000000**" has to be an emphasised swatch, not a lost one.
             * This cannot recurse: the inner text has no ** left in it.
             */
            <strong key={key} className="font-medium">
                <RichText text={match.replace(/\*\*/g, '')} />
            </strong>
        ),
    },
    {
        pattern: HEX,
        render: (match, key) => <ColorBadge key={key} value={match} />,
    },
];

/**
 * Split `text` on one pattern, keeping what matched.
 *
 * Runs over the nodes produced so far rather than over the string, so a second
 * matcher sees the parts the first one left alone and never reaches inside a
 * badge it already made.
 */
function applyMatcher(nodes: React.ReactNode[], matcher: Matcher, tag: string): React.ReactNode[] {
    const out: React.ReactNode[] = [];

    nodes.forEach((node, nodeIndex) => {
        if (typeof node !== 'string') {
            out.push(node);
            return;
        }

        // `lastIndex` persists on a global regex between calls, so it is reset
        // rather than trusted — otherwise the second bubble starts matching
        // from wherever the first one stopped.
        matcher.pattern.lastIndex = 0;

        let cursor = 0;
        let match: RegExpExecArray | null;

        while ((match = matcher.pattern.exec(node)) !== null) {
            if (match.index > cursor) out.push(node.slice(cursor, match.index));
            out.push(matcher.render(match[0], `${tag}-${nodeIndex}-${match.index}`));
            cursor = match.index + match[0].length;
        }

        if (cursor < node.length) out.push(node.slice(cursor));
    });

    return out;
}

export const RichText: React.FC<{ text: string }> = ({ text }) => {
    const nodes = MATCHERS.reduce<React.ReactNode[]>(
        (acc, matcher, i) => applyMatcher(acc, matcher, `m${i}`),
        [text],
    );

    return <>{nodes}</>;
};
